import { getConfig } from '@/utils/config/config';
import { ROUTES } from '@/utils/constants';
import { AMPLITUDE_PROPERTIES_MAP, type AmplitudePropertiesMap } from './amplitude-properties.types';
import { getCookieValue } from '@/utils/cookies/client';
import { COOKIES } from '@/utils/cookies';
import { PRODUCT_EVENT_FIELDS } from './overrides';
import type { IdentityStorageType } from '@amplitude/analytics-types';
import type { UpdatedJackpotTier } from '@/types/zones/slices/site-wide-jackpot-slice';
import type { AmplitudeGlobalKeys } from './amplitude-properties.types';

export const getProduct = (): AmplitudeGlobalKeys['Product'] => {
    const product = process.env.PRODUCT;
    switch (product) {
        case 'casino':
            return 'casino_standalone_lobby';
        case 'poker':
            return 'poker_standalone_lobby';
        case 'xsell':
            return 'casino_xsell_lobby';
        default:
            console.warn('[Amplitude] Unknown product', product);
            return undefined;
    }
};

export const getAndroidDistribution = () => {
    const ua = (navigator?.userAgent || '').toLowerCase();
    switch (true) {
        case ua.includes('selfdist'):
            return 'side-load';
        case ua.includes('playstore'):
            return 'play-store';
        default:
            return '';
    }
};

export const getSiteVersion = ({ isMobile, isWrapper }: { isMobile: boolean; isWrapper: boolean }) => {
    switch (true) {
        case isMobile && !isWrapper:
            return 'mobile_web';
        case isMobile && isWrapper:
            return 'native';
        default:
            return 'desktop';
    }
};

export const getSitePlatform = ({
    isMobile,
    isAndroidWrapper,
    isIosWrapper
}: {
    isMobile: boolean;
    isAndroidWrapper: boolean;
    isIosWrapper: boolean;
}) => {
    const isWrapper = isAndroidWrapper || isIosWrapper;
    switch (true) {
        case isMobile && !isWrapper:
            return 'mobile_web';
        case isMobile && isWrapper:
            return isIosWrapper ? 'ios' : 'android';
        default:
            return 'desktop';
    }
};

const removeEmojis = (str: string) => {
    const emojiRE =
        /([\u2700-\u27BF]|[\uE000-\uF8FF]|\uD83C[\uDC00-\uDFFF]|\uD83D[\uDC00-\uDFFF]|[\u2011-\u26FF]|\uD83E[\uDD10-\uDDFF])/g;
    return emojiRE.test(str) ? str.replace(emojiRE, '').trim().replace(/\s+/g, ' ') : str;
};

export const toSnakeCase = (str: string) =>
    str && str?.length ? removeEmojis(str).toLowerCase().replace(/[-\s]/g, '_') : '';

export const toTitleCase = (pathname: string): string => {
    const segments = pathname.split('/').filter(Boolean);
    const lastSegment = segments[segments.length - 1] || '';
    return lastSegment && lastSegment.length
        ? lastSegment
              .replace(/[-_]/g, ' ')
              .split(' ')
              .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
              .join(' ')
        : '';
};

export const getPageName = (): string => {
    // https://fanduel.atlassian.net/wiki/spaces/FAW/pages/307759514092/Casino+Amplitude+Migration+-+Page+View
    const pathname = window.location.pathname;

    switch (true) {
        case pathname === '/':
            return 'home';
        case pathname.startsWith(ROUTES.CATEGORY_HUB):
            return 'epic_hub';
        case pathname.startsWith('/c/'):
            return 'category';
        case pathname.startsWith('/game/'):
            return 'info';
        case pathname === '/rewards/':
            return 'promotions';
        case pathname.startsWith('/rewards/'):
            return 'promo_offer';
        default:
            return (pathname.split('/').pop() || '').replace(/-/g, '_');
    }
};

export const getAmplitudeKey = () => {
    const config = getConfig();
    return config?.services?.amplitudeKey;
};

export const getAmplitudeBrowserOptions = ({ deviceId, sessionId }: { deviceId: string; sessionId: string }) => {
    return {
        autocapture: {
            attribution: { resetSessionOnNewCampaign: true },
            sessions: false,
            pageViews: false,
            formInteractions: false,
            fileDownloads: false,
            elementInteractions: false,
            pageUrlEnrichment: false,
            networkTracking: false,
            webVitals: false,
            frustrationInteractions: false
        },
        fetchRemoteConfig: false,
        identityStorage: 'localStorage' as IdentityStorageType,
        minIdLength: 1,
        deviceId,
        sessionId: Number(sessionId)
    };
};

export const getEventOptions = () => {
    const sessionId = Number(getCookieValue(COOKIES.AMP_SESSION_ID)); // this is string in prod but lib wants number
    return sessionId ? { session_id: sessionId } : {};
};

export const getMappedEventProperties = (event: Partial<AmplitudePropertiesMap>) => {
    const mappedProperties = Object.keys(event).reduce((acc, key) => {
        const mappedKey = AMPLITUDE_PROPERTIES_MAP[key as keyof typeof AMPLITUDE_PROPERTIES_MAP];
        if (mappedKey) {
            acc[mappedKey as keyof AmplitudePropertiesMap] = event[
                key as keyof AmplitudePropertiesMap
            ] as AmplitudePropertiesMap[keyof AmplitudePropertiesMap];
        } else {
            console.warn(`[Amplitude] No mapped key found for event property: ${key}`);
        }
        return acc;
    }, {} as AmplitudePropertiesMap);

    return mappedProperties;
};

export const removeNulls = (props: Record<string, any>) => {
    const nonNullProps = props;
    for (const key in nonNullProps) {
        if (nonNullProps[key] === null || nonNullProps[key] === '' || nonNullProps[key] === undefined) {
            delete nonNullProps[key];
        }
    }
    return nonNullProps;
};

export const getMultiplierLabel = (multiplier: number): string => `${multiplier}x`;

export const getJackpotData = (tiers: UpdatedJackpotTier[]) => {
    return tiers?.reduce((acc, tier) => {
        return {
            ...acc,
            [`FDC Jackpot ${tier?.short_tier_name?.[0]?.text} Pot Amount`]: tier?.currentAmount
        };
    }, {});
};

export const getEventPropsBuilder = <T extends (...args: any[]) => any>(originalImplementation: T): T => {
    return ((...originalArguments: Parameters<T>) => {
        let eventProps = originalImplementation(...originalArguments);

        const eventName = eventProps?.event;
        if (!eventName) return eventProps;

        const eventOverride = PRODUCT_EVENT_FIELDS[process.env.PRODUCT]?.[eventName]?.override;

        if (eventOverride) {
            eventProps = eventOverride(eventProps, ...originalArguments);
        }

        return eventProps;
    }) as T;
};
